import { useEffect, useMemo, useState } from 'react';
import { MONTHS_FULL, MONTHS_FULL_FOR_SELECT } from '@app/contstants/months';
import { Tooltip } from 'react-tooltip';
import Section from '@widgets/components/section';
import SingleSelect from '@widgets/single-select';
import {
  Table,
  TableBody,
  TableNestableExpandIcon,
  TableNestableRow,
  TableNestableTd,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@widgets/table';
import { useManagerBonusClientDaily } from '@app/queries/managers-performance/useManagerBonusClientDaily';
import { useManagerBonusClients } from '@app/queries/managers-performance/useManagerBonusClients';
import {
  IManagerBonusClientDaily,
  IManagerBonusClientMonthly,
} from '@entities/managersPerformance/IManagerBonus';
import { formatBtc } from './formatters';

type Props = {
  managerId: number | null | undefined;
  month: number;
  year: number;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
};

const ASSIGNMENT_KIND_LABELS: Record<string, string> = {
  new_client: 'Первичное закрепление',
  resignation_transfer: 'Передан после ухода предыдущего менеджера',
  reassignment_transfer: 'Переназначение менеджера',
};

export const SalaryTable = ({ managerId, month, year, onMonthChange, onYearChange }: Props) => {
  const [managerBonusClients, setManagerBonusClients] = useState<IManagerBonusClientMonthly[]>([]);
  const [openedClientIds, setOpenedClientIds] = useState<number[]>([]);
  const [clientDailyDetails, setClientDailyDetails] = useState<Record<string, IManagerBonusClientDaily[]>>({});

  const managerBonusClientsFetcher = useManagerBonusClients();
  const managerBonusClientDailyFetcher = useManagerBonusClientDaily();
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear].map(optionYear => ({
      value: optionYear.toString(),
      label: optionYear.toString(),
    }));
  }, []);

  useEffect(() => {
    (async () => {
      if (managerId === null || managerId === undefined) {
        setManagerBonusClients([]);
        return;
      }

      try {
        const data = await managerBonusClientsFetcher.fetch({
          month,
          managerId,
          year,
        });
        setManagerBonusClients(data);
      } catch (e) {
        console.error('Error fetching manager bonus clients', e);
        setManagerBonusClients([]);
      }
    })();
  }, [managerId, month, year]);

  useEffect(() => {
    setOpenedClientIds([]);
    setClientDailyDetails({});
  }, [managerId, month, year]);

  const totals = useMemo(() => {
    const init = { reward: 0, bonus: 0 };
    if (!managerBonusClients.length) return init;

    return managerBonusClients.reduce((acc, it) => {
      acc.reward += Number(it.total_pool_reward_btc || 0);
      acc.bonus += Number(it.total_bonus_btc || 0);
      return acc;
    }, init);
  }, [managerBonusClients]);

  const toggleClient = async (clientId: number) => {
    if (managerId === null || managerId === undefined) return;

    const detailsCacheKey = `${managerId}_${year}_${month}_${clientId}`;

    if (openedClientIds.includes(clientId)) {
      setOpenedClientIds(prev => prev.filter(id => id !== clientId));
      return;
    }

    setOpenedClientIds(prev => [...prev, clientId]);
    if (clientDailyDetails[detailsCacheKey]) return;

    try {
      const data = await managerBonusClientDailyFetcher.fetch({
        managerId,
        month,
        year,
        clientId,
      });
      setClientDailyDetails(prev => ({ ...prev, [detailsCacheKey]: data }));
    } catch (e) {
      console.error('Error fetching manager bonus client daily details', e);
    }
  };

  if (managerId === null || managerId === undefined) {
    return null;
  }

  return (
    <Section>
      <div className="flex items-center justify-between w-full mb-[10px] white">
        <span className="font-semibold text-[20px]">Клиенты</span>
        <div className="flex items-center gap-[10px]">
          <SingleSelect
            value={{
              value: month.toString(),
              label: MONTHS_FULL[month - 1],
            }}
            title=""
            isLoading={false}
            options={MONTHS_FULL_FOR_SELECT}
            onChange={option => {
              if (!option?.value) return;
              onMonthChange(+option.value);
            }}
            textSize="16px"
            hideClearIcon={true}
          />
          <SingleSelect
            value={{
              value: year.toString(),
              label: year.toString(),
            }}
            title=""
            isLoading={false}
            options={yearOptions}
            onChange={option => {
              if (!option?.value) return;
              onYearChange(+option.value);
            }}
            textSize="16px"
            hideClearIcon={true}
            width={110}
          />
        </div>
      </div>
      <Table>
        <TableThead>
          <TableTr>
            <TableTh title="" className="w-[6%]" />
            <TableTh title="Клиент" className="w-[24%] text-left" />
            <TableTh title="Источник" className="w-[20%] text-left" />
            <TableTh title="Дней" className="w-[10%] text-left" />
            <TableTh title="Reward, BTC" className="w-[20%] text-left" />
            <TableTh title="Бонус, BTC" className="w-[20%]" />
          </TableTr>
        </TableThead>
      </Table>

      {managerBonusClients.map(row => (
        <div key={row.client_id}>
          <TableNestableRow>
            <TableNestableTd className="w-[6%]">
              <TableNestableExpandIcon
                shown={openedClientIds.includes(row.client_id)}
                onClick={() => toggleClient(row.client_id)}
              />
            </TableNestableTd>
            <TableNestableTd className="w-[24%]">
              <button className="text-left underline-offset-2 hover:underline" onClick={() => toggleClient(row.client_id)}>
                {row.client_name}
              </button>
            </TableNestableTd>
            <TableNestableTd className="w-[20%]">{row.client_source ?? '-'}</TableNestableTd>
            <TableNestableTd className="w-[10%]">{row.days_count}</TableNestableTd>
            <TableNestableTd className="w-[20%]">{formatBtc(row.total_pool_reward_btc)}</TableNestableTd>
            <TableNestableTd className="w-[20%] justify-end">{formatBtc(row.total_bonus_btc)}</TableNestableTd>
          </TableNestableRow>

          {openedClientIds.includes(row.client_id) ? (
            <Table>
              <TableThead className="static">
                <TableTr>
                  <TableTh title="Дата" className="w-[20%] text-left" />
                  <TableTh title="Основание" className="w-[20%] text-left" />
                  <TableTh title="Стаж" className="w-[15%] text-left" />
                  <TableTh title="Ставка / Reward" className="w-[25%] text-left" />
                  <TableTh title="Бонус, BTC" className="w-[20%]" />
                </TableTr>
              </TableThead>
              <TableBody>
                {(clientDailyDetails[`${managerId}_${year}_${month}_${row.client_id}`] ?? []).map((daily, index) => (
                  <TableTr key={`${row.client_id}_${daily.date}_${index}`}>
                    <TableTd title={daily.date} className="w-[20%]" />
                    <TableTd
                      title={
                        <div className="flex flex-col items-start gap-[2px]">
                          <span>{ASSIGNMENT_KIND_LABELS[daily.assignment_kind] ?? daily.assignment_kind}</span>
                          {daily.assignment_comment ? (
                            <>
                              <span
                                id={`manager-bonus-comment-${row.client_id}-${daily.date}-${index}`}
                                className="text-[12px] text-[#959595] max-w-[220px] truncate block cursor-help"
                              >
                                Комментарий: {daily.assignment_comment}
                              </span>
                              <Tooltip
                                anchorSelect={`#manager-bonus-comment-${row.client_id}-${daily.date}-${index}`}
                                content={daily.assignment_comment}
                              />
                            </>
                          ) : null}
                        </div>
                      }
                      className="w-[20%]"
                    />
                    <TableTd title={`${daily.tenure_months} мес`} className="w-[15%]" />
                    <TableTd title={`${daily.rate_percent}% • ${formatBtc(daily.pool_reward_btc)}`} className="w-[25%]" />
                    <TableTd title={formatBtc(daily.bonus_btc)} className="w-[20%]" align="right" />
                  </TableTr>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </div>
      ))}

      {managerBonusClients.length ? (
        <Table>
          <TableBody>
            <TableTr withoutUnderline>
              <TableTd title="" className="w-[6%]" />
              <TableTd title="" className="w-[24%]" />
              <TableTd title="" className="w-[20%]" />
              <TableTd title="" className="w-[10%]" />
              <TableTd title={<span className="text-[#189CD8]">{formatBtc(totals.reward)}</span>} className="w-[20%]" />
              <TableTd
                title={<span className="text-[#189CD8]">{formatBtc(totals.bonus)}</span>}
                className="w-[20%]"
                align="right"
              />
            </TableTr>
          </TableBody>
        </Table>
      ) : null}
    </Section>
  );
};
